export type Plan = 'basic' | 'standard' | 'premium';

/**
 * Feature definitions with their plan requirements.
 */
export const SUBSCRIPTION_FEATURES = {
  UNLIMITED_SCHEDULES: {
    id: 'save_schedules',
    title: 'Save Schedules',
    description: 'Save and manage multiple parenting schedules',
    plans: ['standard', 'premium'],
  },
  EXPORT_CALENDAR: {
    id: 'export_calendar',
    title: 'Export Schedules',
    description: 'Export to .ics, PDF, and DOCX formats',
    plans: ['standard', 'premium'],
  },
  TIME_CALCULATOR: {
    id: 'time_calculator',
    title: 'Parenting Time Calculator',
    description: 'See exact parenting time percentages and overnights',
    plans: ['standard', 'premium'],
  },
  FINANCIAL_CALCULATOR: {
    id: 'financial_calculator',
    title: 'Child Support Estimator',
    description: 'Estimate monthly support based on your custody split and state guidelines',
    plans: ['basic', 'standard', 'premium'],
  },
  CUSTODY_ANALYSIS: {
    id: 'custody_analysis',
    title: 'Custody Analysis Dashboard',
    description: 'Time distribution, transitions, and schedule impact analysis',
    plans: ['basic', 'standard', 'premium'],
  },
  SCENARIO_COMPARE: {
    id: 'scenario_compare',
    title: 'Scenario Comparison',
    description: 'Compare alternate custody schedules, parenting-time gaps, and financial impact',
    plans: ['premium'],
  },
  LEGAL_TEMPLATES: {
    id: 'legal_templates',
    title: 'Legal Templates',
    description: 'Attorney-review-ready templates for custody agreements',
    plans: ['premium'],
  },
  EVIDENCE_REPORT: {
    id: 'evidence_report',
    title: 'Evidence Report',
    description: 'Generate detailed evidence reports with overnights, exchanges, and compliance metrics',
    plans: ['premium'],
  },
  SUPPORT_CALCULATOR_FULL: {
    id: 'support_calculator_full',
    title: 'Detailed Support Worksheet',
    description: 'Show detailed state-guideline worksheet lines inside the schedule analysis',
    plans: ['premium'],
  },
} as const;

export type FeatureId = keyof typeof SUBSCRIPTION_FEATURES;

// Kept in config so route guards, hooks, and tests share one access matrix.
export const FEATURE_ACCESS: Record<Plan, string[]> = {
  basic: [
    'create_draft',
    'basic_calendar',
    'financial_calculator',
    'custody_analysis',
  ],
  standard: [
    'create_draft',
    'basic_calendar',
    'financial_calculator',
    'custody_analysis',
    'time_calculator',
    'export_calendar',
    'export_pdf',
    'export_docx',
    'save_schedules',
    'save_limit_3',
  ],
  premium: [
    'create_draft',
    'basic_calendar',
    'financial_calculator',
    'custody_analysis',
    'time_calculator',
    'export_calendar',
    'export_pdf',
    'export_docx',
    'save_schedules',
    'save_limit_10',
    'legal_templates',
    'scenario_compare',
    'lawyer_formatting',
    'editable_templates',
    'mediation_reports',
    'evidence_report',
    'support_calculator_full',
  ],
};
